<template>
  <ElDialog
    class="reviews-filter"
    :visible="value"
    :title="$t('APP.REVIEWS.REVIEW_FILTER')"
    width="30%"
    @close="emitInputEvent"
  >
    <ElForm label-position="top">
      <ElFormItem :label="$t('APP.REVIEWS.FILTERS.DATE_RANGE')">
        <ElRadioGroup v-model="dateRangeType">
          <ElRadioButton label="all">
            {{ $t('APP.REVIEWS.FILTERS.ALL') }}
          </ElRadioButton>
          <ElRadioButton label="range">
            {{ $t('APP.REVIEWS.FILTERS.CUSTOM_RANGE') }}
          </ElRadioButton>
        </ElRadioGroup>
      </ElFormItem>

      <template v-if="dateRangeType === 'range'">
        <ElFormItem :label="$t('FROM_DATE')" class="reviews-filter__pickers">
          <UiDatePicker
            :disabled="filterDates"
            :default-date="lastMonthDate"
            @select-date="setFromDate"
          />
        </ElFormItem>

        <ElFormItem :label="$t('TO_DATE')" class="reviews-filter__pickers">
          <UiDatePicker
            :disabled="filterDates"
            :default-date="currentDate"
            @select-date="setToDate"
          />
        </ElFormItem>
      </template>

      <ElFormItem :label="$t('APP.REVIEWS.FILTER_BY_REPLY')">
        <ElRadioGroup v-model="is_replied">
          <ElRadioButton label="">
            {{ $t('APP.REVIEWS.FILTERS.ALL') }}
          </ElRadioButton>
          <ElRadioButton label="true">
            {{ $t('APP.REVIEWS.FILTERS.WITH_REPLY') }}
          </ElRadioButton>
          <ElRadioButton label="false">
            {{ $t('APP.REVIEWS.FILTERS.WITHOUT_REPLY') }}
          </ElRadioButton>
        </ElRadioGroup>
      </ElFormItem>

      <ElFormItem
        v-if="packages"
        :label="$t('APP.REVIEWS.FILTERS.APP_VERSION')"
      >
        <AlternativeSelect
          v-model="app_version_code"
          clearable
          name="version_code"
          key-prop="token"
          value-prop="versionCode"
          :label-prop="getPackageVersionsLabel"
          :items="packages"
          :placeholder="$t('CHOOSE')"
        />
      </ElFormItem>

      <ElFormItem :label="$t('APP.REVIEWS.FILTERS.SDK_VERSION')">
        <AlternativeSelect
          v-model="sdk_version"
          clearable
          name="sdk_version"
          key-prop="key"
          value-prop="key"
          :label-prop="getPackageSdkLabel"
          :items="sdkVersions"
          :placeholder="$t('CHOOSE')"
        />
      </ElFormItem>

      <ElFormItem :label="$t('APP.REVIEWS.FILTERS.RATE_RATE')">
        <ElSlider
          v-model="rateRange"
          range
          show-stops
          show-tooltip
          :step="1"
          :min="1"
          :max="5"
        />
      </ElFormItem>

      <ElButton type="primary" @click.native="emitAppliedFilters">
        {{ $t('APPLY') }}
      </ElButton>

      <ElButton type="text" :plain="true" @click.native="emitInputEvent">
        {{ $t('CANCEL') }}
      </ElButton>
    </ElForm>
  </ElDialog>
</template>

<script>
import UiDatePicker from '@@/layout/UiDatePicker';
import AlternativeSelect from '@@/common/AlternativeSelect';
import { RELEASES } from '@/store/types';

import { nonEmptyProps } from '@/utils/object';

import form from '@/mixins/form';
import modal from '@/mixins/modal';
import packageNameMixin from '@/mixins/packageName';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ReviewsFilterModal',

  components: {
    UiDatePicker,
    AlternativeSelect,
  },

  mixins: [modal, form, packageNameMixin],

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentFilters: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      dateRangeType: 'all',
      dateRange: {
        min_date: '',
        max_date: '',
      },

      rateRange: [1, 5],
      is_replied: '',
      app_version_code: null,
      sdk_version: null,
    };
  },

  computed: {
    ...mapGetters({ releases: RELEASES }),
    lastMonthDate() {
      const date = new Date();

      return date.setMonth(date.getMonth() - 1);
    },

    currentDate() {
      return Date.now();
    },

    packages() {
      if (!this.releases) return null;

      return this.releases.reduce((result, release) => {
        const packageItem = release.packages ? release.packages : release;
        return result.concat(packageItem);
      }, []);
    },

    sdkVersions() {
      return [
        { key: 34, label: '14' },
        { key: 33, label: '13' },
        { key: 32, label: '12.1' },
        { key: 31, label: '12' },
        { key: 30, label: '11' },
        { key: 29, label: '10' },
        { key: 28, label: '9' },
        { key: 27, label: '8.1' },
        { key: 26, label: '8.0' },
        { key: 25, label: '7.1.2' },
        { key: 24, label: '7.0' },
        { key: 23, label: '6.0' },
        { key: 22, label: '5.1' },
        { key: 21, label: '5.0' },
        { key: 20, label: '4.4' },
        { key: 19, label: '4.4' },
        { key: 18, label: '4.3' },
        { key: 17, label: '4.2' },
        { key: 16, label: '4.1' },
        { key: 15, label: '4.0.3' },
        { key: 14, label: '4.0' },
        { key: 13, label: '3.2' },
        { key: 12, label: '3.1' },
        { key: 11, label: '3.0' },
        { key: 10, label: '2.3.3' },
        { key: 9, label: '2.3' },
        { key: 8, label: '2.2' },
        { key: 7, label: '2.1' },
        { key: 6, label: '2.0.1' },
        { key: 5, label: '2.0' },
        { key: 4, label: '1.6' },
        { key: 3, label: '1.5' },
        { key: 2, label: '1.1' },
        { key: 1, label: '1.0' },
      ];
    },
  },

  watch: {
    value() {
      const {
        min_date = '',
        max_date = '',
        min_rate = 1,
        max_rate = 5,
        is_replied = '',
        sdk_version = null,
        app_version_code = null,
      } = this.currentFilters || {};

      if (min_date && max_date) {
        this.dateRange.min_date = min_date;
        this.dateRange.max_date = max_date;
        this.dateRangeType = 'range';
      } else {
        this.dateRangeType = 'all';
      }

      this.is_replied = is_replied;
      this.app_version_code = app_version_code;
      this.sdk_version = sdk_version;
      this.rateRange = [min_rate, max_rate];
    },
  },

  created() {
    if (!this.releases) {
      this.getReleases();
    }
  },

  methods: {
    ...mapActions({ dispatchPackages: RELEASES }),
    emitInputEvent() {
      this.$emit('input', false);
    },

    setFromDate(selectedDate) {
      this.$set(this.dateRange, 'min_date', selectedDate);
    },

    setToDate(selectedDate) {
      this.$set(this.dateRange, 'max_date', selectedDate);
    },

    getPackageVersionsLabel({ versionName, versionCode }) {
      return `${versionName} (${versionCode})`;
    },

    getPackageSdkLabel({ key, label }) {
      return `${label} (${key})`;
    },

    getReleases() {
      return this.dispatchPackages(this.packageName).catch(message => {
        if (message) {
          this.$message.error(message);
        }
      });
    },

    emitAppliedFilters() {
      const hasCustomDateRange = this.dateRangeType === 'range';

      const hasSpecifiedRate = !(
        this.rateRange[0] === 1 && this.rateRange[1] === 5
      );

      const rateRange = {
        min_rate: this.rateRange[0],
        max_rate: this.rateRange[1],
      };

      const filters = {
        ...(hasCustomDateRange && this.dateRange),
        ...(hasSpecifiedRate && rateRange),
        is_replied: this.is_replied,
        app_version_code: this.app_version_code,
        sdk_version: this.sdk_version,
      };

      this.$emit('set-filters', nonEmptyProps(filters));
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss">
.reviews-filter {
  &__pickers {
    max-width: 500px;
  }
}
</style>
